import React, { useState } from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import CCText from '@/Atoms/CCText';
import CCColors, { updateColorWithOpacity } from '@/Utils/CCColors';
import { ScreenName } from '@/navigators/StackConstants';
import { transition } from '@/Utils/motion';
import { insetRadius, radii } from '@/Utils/radius';
import { dsIcons } from '../../designIcons';
import {
  TIME_CONTROLS,
  useRecommendedTimeControl,
} from '../../timeControls';

export type ActionVariant = 'tile' | 'hero';

interface ShellProps {
  variant: ActionVariant;
  bg?: string;
  heroGradient?: [string, string];
  heroArt?: [any, any];
  accent: string;
  ink?: string;
  icon: any;
  title: string;
  desc: string;
  badge?: string;
  onPress?: () => void;
  rightRail?: (accent: string) => React.ReactNode;
  elevated?: boolean;
  /**
   * Tile only: the grid's row goes to a single column at narrow widths, so
   * the card's own fixed calc(50%-5px) width — sized for TWO cards sharing a
   * row — has to give way to full width too. Without this, tiles stayed
   * pinned to half-width in a now-vertical stack, on top of a row that never
   * stacked its DIRECTION at all (see TrainAndSolveGrid's own stacked prop).
   */
  stacked?: boolean;
}

function ActionCard({
  variant,
  bg,
  heroGradient,
  heroArt,
  accent,
  ink,
  icon,
  title,
  desc,
  badge,
  onPress,
  rightRail,
  elevated,
  stacked,
}: ShellProps) {
  const hero = variant === 'hero';
  const textColor = hero && ink ? ink : CCColors.TextColor.Dark;

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: any) => [
        styles.card,
        hero ? styles.cardHero : styles.cardTile,
        !hero && stacked && styles.cardTileStacked,
        // Tile already overrides flexGrow to 0 unconditionally (cardTile uses
        // an explicit width instead), so this only ever matters for hero:
        // hero has no explicit width, it fills its row purely via flexGrow:1
        // — which, once the row goes to a column, targets the vertical axis
        // instead and stretches the card to fill leftover page height. Same
        // bug FocusCards' own Class/Assignment cards had, same fix.
        hero && stacked && { flexGrow: 0 },
        { backgroundColor: bg },
        elevated && { zIndex: 2 },
        onPress && { cursor: 'pointer' },
        hovered && styles.cardHovered,
        pressed && onPress && styles.cardPressed,
      ]}>
      {({ hovered }: any) => (
        <>
          <View
            style={[
              StyleSheet.absoluteFill,
              { borderRadius: insetRadius(), overflow: 'hidden' },
            ]}
            pointerEvents="none">
            {hero && heroGradient && (
              <LinearGradient
                colors={heroGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
              />
            )}
            {hero && heroArt && (
              <>
                <Image
                  source={heroArt[0]}
                  style={styles.artPiece}
                  resizeMode="contain"
                />
                <Image
                  source={heroArt[1]}
                  style={styles.artPieceSmall}
                  resizeMode="contain"
                />
              </>
            )}
            <LinearGradient
              colors={
                hero
                  ? ['rgba(255,255,255,0.28)', 'rgba(255,255,255,0)']
                  : ['rgba(255,255,255,0.9)', 'rgba(255,255,255,0)']
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 0.9, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
          </View>
          {!hero && <View style={[styles.accent, { backgroundColor: accent }]} />}
          <View style={[styles.head, rightRail && styles.headWithRail]}>
            <View
              style={[
                styles.iconChip,
                hero
                  ? styles.iconChipHero
                  : { borderColor: updateColorWithOpacity(accent, 0.25) },
                !hero && hovered && { borderColor: accent },
              ]}>
              <Image source={icon} style={styles.iconImg} resizeMode="contain" />
            </View>

            <View style={styles.copy}>
              <View style={styles.titleRow}>
                <CCText
                  fontSize={hero ? 15.5 : 14.5}
                  fontWeight="bold"
                  color={textColor}
                  showEllipses
                  style={{ flexShrink: 1 }}>
                  {title}
                </CCText>
                {badge && (
                  <View style={[styles.badge, { backgroundColor: accent }]}>
                    <CCText fontSize={9} fontWeight="bold" color={CCColors.White}>
                      {badge}
                    </CCText>
                  </View>
                )}
              </View>
              <CCText
                fontSize={11.5}
                color={hero ? textColor : CCColors.TextColor.Grey}
                numberOfLines={2}
                style={[styles.desc, hero && { opacity: 0.75 }]}>
                {desc}
              </CCText>
            </View>

            {rightRail ? (
              <View style={styles.rightRail}>{rightRail(accent)}</View>
            ) : (
              <View
                style={[
                  styles.arrowChip,
                  { borderColor: updateColorWithOpacity(accent, 0.25) },
                  hovered && { backgroundColor: accent, borderColor: accent },
                ]}>
                <MaterialCommunityIcon
                  name="arrow-right"
                  size={22}
                  color={hovered ? CCColors.White : accent}
                />
              </View>
            )}
          </View>
        </>
      )}
    </Pressable>
  );
}

function ActionButton({
  label,
  accent,
  onPress,
  ghost,
  grow,
}: {
  label: string;
  accent: string;
  onPress: () => void;
  ghost?: boolean;
  grow?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: any) => [
        styles.btn,
        grow && { flex: 1 },
        ghost
          ? [styles.btnGhost, { borderColor: updateColorWithOpacity(accent, 0.45) }]
          : { backgroundColor: accent },
        ghost && hovered && { backgroundColor: updateColorWithOpacity(accent, 0.12) },
        hovered && !ghost && { opacity: 0.9 },
        pressed && { transform: [{ scale: 0.97 }] },
      ]}>
      <CCText
        fontSize={12}
        fontWeight="bold"
        textAlign="center"
        color={ghost ? accent : CCColors.White}>
        {label}
      </CCText>
    </Pressable>
  );
}

interface CardProps {
  navigation: any;
  variant?: ActionVariant;
  /** Tile only — see ShellProps.stacked. */
  stacked?: boolean;
}

export function TrainersCard({ navigation, variant = 'tile', stacked }: CardProps) {
  return (
    <ActionCard
      variant={variant}
      stacked={stacked}
      bg={variant === 'hero' ? '#8FB89B' : '#E4F6EB'}
      ink="#1F3A29"
      accent={CCColors.Green}
      icon={dsIcons.learning}
      title="Trainers"
      desc="Calculation, intuition and square drills"
      onPress={() => navigation.navigate(ScreenName.Visualisation)}
    />
  );
}

export function PositionsCard({ navigation, variant = 'tile', stacked }: CardProps) {
  return (
    <ActionCard
      variant={variant}
      stacked={stacked}
      bg={variant === 'hero' ? '#8FB4DC' : '#E9F3FF'}
      ink="#1B3550"
      accent={CCColors.Blue}
      icon={dsIcons.positionsTrainer}
      title="Positions"
      desc="Play the right move from your games"
      badge="Daily"
      onPress={() => navigation.navigate(ScreenName.Puzzles)}
    />
  );
}

export function CalculationTrainerCard({ navigation, stacked }: CardProps) {
  return (
    <ActionCard
      variant="tile"
      stacked={stacked}
      bg="#FFEFEE"
      ink="#5C2416"
      accent={CCColors.Red}
      icon={dsIcons.calculationTrainer}
      title="Calculation Trainer"
      desc="Guess next move"
      onPress={() =>
        navigation.navigate('VisualisationTrainer', { selected: 'calculation' })
      }
    />
  );
}

export function IntuitionTrainerCard({ navigation, stacked }: CardProps) {
  return (
    <ActionCard
      variant="tile"
      stacked={stacked}
      bg="#E4F6EB"
      ink="#1F3A29"
      accent={CCColors.Green}
      icon={dsIcons.learning}
      title="Intuition Trainer"
      desc="Perform against GM"
      onPress={() =>
        navigation.navigate('VisualisationTrainer', { selected: 'intuition' })
      }
    />
  );
}

export function SquareTrainerCard({ navigation, stacked }: CardProps) {
  return (
    <ActionCard
      variant="tile"
      stacked={stacked}
      bg="#F3EEFF"
      ink="#3B2149"
      accent={CCColors.Pink}
      icon={dsIcons.intuitionTrainer}
      title="Square Trainer"
      desc="Guess the square"
      onPress={() =>
        navigation.navigate('VisualisationTrainer', { selected: 'square' })
      }
    />
  );
}

export function PlayCard({ navigation, variant = 'tile', stacked }: CardProps) {
  return variant === 'hero' ? (
    <PlayCardHero navigation={navigation} stacked={stacked} />
  ) : (
    <ActionCard
      variant={variant}
      stacked={stacked}
      bg="#EDF5E9"
      ink="#25331F"
      accent="#4A5A44"
      icon={dsIcons.gamePlay}
      title="Play"
      desc="Find an opponent at your level"
      onPress={() => navigation.navigate(ScreenName.PlayChess)}
    />
  );
}

function PlayCardHero({ navigation, stacked }: { navigation: any; stacked?: boolean }) {
  const recommended = useRecommendedTimeControl();
  const [picked, setPicked] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const selected = picked ?? recommended;

  return (
    <ActionCard
      variant="hero"
      stacked={stacked}
      bg="#F2A08A"
      heroArt={[dsIcons.whiteKnight, require('@/Assets/ChessPieces/White-Pawn.svg')]}
      ink="#5C2416"
      accent="#8B3A1F"
      icon={dsIcons.gamePlay}
      title="Play"
      desc="Find an opponent at your level"
      elevated
      rightRail={accent => (
        <>
          {/* Explicit zIndex here, not just on the menu itself: this wrapper
              and the Play button below are siblings, and relying on the
              menu's zIndex:50 alone to outrank the button's default (auto)
              stacking wasn't reliable in practice — the button still painted
              on top. An explicit ordinal pair on both siblings removes that
              ambiguity outright. */}
          <View style={{ position: 'relative', width: 132, zIndex: 1 }}>
            <Pressable
              onPress={() => setOpen(o => !o)}
              style={({ hovered }: any) => [
                styles.select,
                hovered && { borderColor: accent },
              ]}>
              <MaterialCommunityIcon
                name="clock-outline"
                size={14}
                color={accent}
              />
              <CCText
                fontSize={12}
                fontWeight="bold"
                color={CCColors.TextColor.Dark}
                style={{ flex: 1 }}>
                {TIME_CONTROLS.find(t => t.control === selected)?.label ??
                  selected}
              </CCText>
              <MaterialCommunityIcon
                name={open ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={CCColors.TextColor.Grey}
              />
            </Pressable>

            {open && (
              <View style={styles.menu}>
                {TIME_CONTROLS.map(option => {
                  const active = option.control === selected;
                  return (
                    <Pressable
                      key={option.control}
                      onPress={() => {
                        setPicked(option.control);
                        setOpen(false);
                      }}
                      style={({ hovered }: any) => [
                        styles.menuRow,
                        (hovered || active) && {
                          backgroundColor: updateColorWithOpacity(accent, 0.1),
                        },
                      ]}>
                      <CCText
                        fontSize={12}
                        fontWeight={active ? 'bold' : '500'}
                        color={CCColors.TextColor.Dark}
                        style={{ flex: 1 }}>
                        {option.label}
                      </CCText>
                      <CCText fontSize={10} color={CCColors.TextColor.Grey}>
                        {option.bucket}
                      </CCText>
                      {option.control === recommended && (
                        <View
                          style={[styles.lastPill, { backgroundColor: accent }]}>
                          <CCText
                            fontSize={8.5}
                            fontWeight="bold"
                            color={CCColors.White}>
                            LAST
                          </CCText>
                        </View>
                      )}
                    </Pressable>
                  );
                })}
              </View>
            )}
          </View>

          {/* Plain and static — no hide-while-open, no push-down. zIndex:0,
              explicitly lower than the select-wrapper's 1 (and the menu's own
              50 inside it) above, so the menu reliably paints over this
              button while open instead of leaving it to default stacking. */}
          <View style={{ zIndex: 0 }}>
            <ActionButton
              label="Play"
              accent={accent}
              onPress={() =>
                navigation.navigate(ScreenName.PlayChess, {
                  timeControl: selected,
                  autoJoin: '1',
                })
              }
            />
          </View>
        </>
      )}
    />
  );
}

export function FunChallengeCard({ navigation, variant = 'tile', stacked }: CardProps) {
  const hero = variant === 'hero';
  return (
    <ActionCard
      variant={variant}
      stacked={stacked}
      bg={hero ? '#FACF47' : '#F3EEFF'}
      heroArt={
        hero
          ? [dsIcons.whiteKnight, require('@/Assets/ChessPieces/White-Pawn.svg')]
          : undefined
      }
      ink={hero ? '#4D3F37' : '#3B2149'}
      accent={hero ? '#4D3F37' : CCColors.Pink}
      icon={dsIcons.chessEvents}
      title="Fun Challenge"
      desc="Race the clock solving puzzles solo"
      onPress={() =>
        navigation.navigate(ScreenName.FunChallengesLobby, {
          mode: 'solo',
          autoStart: '1',
        })
      }
    />
  );
}

const styles = StyleSheet.create({
  card: {
    flexGrow: 1,
    minWidth: 0,
    borderRadius: radii.panel,
    position: 'relative',
    overflow: 'visible',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.7)',
    shadowColor: '#4D3F37',
    shadowOpacity: 0.06,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 2 },
    ...transition(170),
  },
  cardTile: {
    paddingLeft: 17,
    paddingRight: 12,
    paddingVertical: 14,
    flexGrow: 0,
    flexShrink: 0,
    width: 'calc(50% - 5px)',
  },
  // Overrides cardTile's width once the grid's row is a column — no sibling
  // to leave half the row for.
  cardTileStacked: { width: '100%' },
  cardHero: {
    paddingLeft: 16,
    paddingRight: 14,
    paddingVertical: 16,
  },
  cardHovered: {
    transform: [{ translateY: -4 }, { scale: 1.015 }],
    shadowOpacity: 0.16,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    borderColor: 'rgba(255,255,255,0.95)',
  },
  cardPressed: {
    transform: [{ scale: 0.985 }],
    opacity: 0.92,
  },
  accent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderTopLeftRadius: insetRadius(),
    borderBottomLeftRadius: insetRadius(),
  },
  artPiece: {
    position: 'absolute',
    right: 6,
    bottom: -26,
    width: 110,
    height: 110,
    opacity: 0.16,
    transform: [{ rotate: '-8deg' }],
  },
  artPieceSmall: {
    position: 'absolute',
    right: 96,
    bottom: -16,
    width: 76,
    height: 76,
    opacity: 0.12,
    transform: [{ rotate: '12deg' }],
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    minHeight: 48,
  },
  headWithRail: { alignItems: 'flex-start' },
  copy: { flex: 1, minWidth: 0 },
  iconChip: {
    width: 42,
    height: 42,
    borderRadius: radii.chip,
    backgroundColor: CCColors.White,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderWidth: 1.5,
    borderColor: 'transparent',
    shadowColor: '#4D3F37',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    ...transition(170),
  },
  iconChipHero: { borderColor: 'rgba(255,255,255,0.9)' },
  iconImg: { width: 24, height: 24 },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  desc: { marginTop: 2 },
  arrowChip: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: CCColors.White,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    shadowColor: '#4D3F37',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    ...transition(170),
  },
  rightRail: {
    alignItems: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: radii.pill,
    justifyContent: 'center',
    cursor: 'pointer',
    ...transition(150),
  },
  btnGhost: {
    backgroundColor: 'rgba(255,255,255,0.75)',
    borderWidth: 1.5,
  },
  select: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 11,
    paddingVertical: 8,
    borderRadius: radii.chip,
    backgroundColor: CCColors.White,
    borderWidth: 1.5,
    borderColor: 'rgba(0,0,0,0.08)',
    cursor: 'pointer',
    ...transition(150),
  },
  menu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: CCColors.White,
    borderRadius: radii.chip,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    shadowColor: '#4D3F37',
    shadowOpacity: 0.18,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    zIndex: 50,
  },
  menuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    cursor: 'pointer',
  },
  lastPill: {
    paddingHorizontal: 5,
    paddingVertical: 1.5,
    borderRadius: radii.badge,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: radii.badge,
    flexShrink: 0,
  },
});
